import { Component, effect, ViewChild, OnInit, inject, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { VideoBackgroundComponent } from 'src/app/shared/ui-common/video-background/video-background.component';
import { ButtonComponent } from 'src/app/shared/ui-common/button/button.component';
import { Router, RouterLink } from '@angular/router';
import { PillComponent } from 'src/app/shared/ui-common/pill/pill.component';
import { MenuItem } from 'src/app/shared/data/models/menu-item.model';
import { FeaturePillMenuComponent } from 'src/app/shared/feature-pill-menu/feature-pill-menu.component';
import { PillService } from 'src/app/shared/data/pill.service';
import { SharedService } from 'src/app/shared/data/shared.service';
import { TranslatePipe } from 'src/app/shared/data/pipes/translate.pipe';
import { TranslationService } from 'src/app/shared/data/translation.service';
import { environment } from 'src/environments/environment';
import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { TosDialogComponent } from 'src/app/shared/ui-common/tos-dialog/tos-dialog.component';
import {
  FeatureInvalidPhoneVerificationComponent
} from 'src/app/shared/feature-invalid-phone-verification/feature-invalid-phone-verification.component';
import { updateDialogWidth } from 'src/app/shared/util/shared.util';

interface AvailableIn {
  textKey: string;
  valueKey: string;
  params?: { count: string };
}

@Component({
  selector: 'app-feature-available-surveys',
  imports: [
    CommonModule,
    VideoBackgroundComponent,
    ButtonComponent,
    PillComponent,
    FeaturePillMenuComponent,
    TranslatePipe,
    RouterLink
  ],
  templateUrl: './feature-available-surveys.component.html',
  styleUrl: './feature-available-surveys.component.scss',
})
export class FeatureAvailableSurveysComponent implements OnInit {
  dialog = inject(Dialog);
  tosDialogWidth!: number;
  tosDialogRef!: DialogRef<unknown, TosDialogComponent>;
  invalidPhoneDialogRef!: DialogRef<unknown, FeatureInvalidPhoneVerificationComponent>;

  @ViewChild('pillMenu') pillMenu!: FeaturePillMenuComponent;

  s3BaseUrl = environment.s3BaseUrl;
  selectedMenuItemId = 1;
  availableIn: AvailableIn = { textKey: '', valueKey: '' };
  headline = 'WebApp.v1.Shared.Pill.Survey.Mind.Title';
  currentVideo = 'mind';
  pillExpanded = false;
  language = 'English';
  showHowdyVideo = false;
  fadeOut = false;
  fadeIn = false;
  allVideoSources: string[] = [];
  buttonIcon = 'fa-solid fa-brain';
  videoFormat = '.mp4';
  who5MenuItemId = -1;
  hideContent = false;

  @HostListener('window:resize')
  onResize(): void {
    this.tosDialogWidth = updateDialogWidth(this.tosDialogRef);
  }

  get decreaseFontSize(): boolean {
    return this.headline.length > 40;
  }

  constructor(
    private router: Router,
    private pillService: PillService,
    private sharedService: SharedService,
    private translationService: TranslationService
  ) {
    effect(() => {
      this.selectedMenuItemId = this.pillService.getSelectedMenuItemId();
      const selectedMenuItem = pillService.getCurrentMenuItem();
      this.videoFormat = this.sharedService.getVideoFormat();
      this.allVideoSources = this.pillService.getMenuItems().map(item => item.video);

      if (!selectedMenuItem) {
        return;
      }

      this.onMenuItemChange(selectedMenuItem);
    });
  }

  async ngOnInit(): Promise<void> {
    this.tosDialogWidth = updateDialogWidth(this.tosDialogRef);
    const account = await this.sharedService.fetchAccountData();
    await this.pillService.loadMenuItems();
    const languageId = this.translationService.getLanguageId();
    this.language = languageId === '1041' ? 'Danish' : 'English';

    if (!account?.hasAcceptedLatestToS) {
      const terms = await this.sharedService.getTermsOfService().then();
      this.openTosDialog(terms);

      return;
    }

    if (account.isInvalidPhoneNumber) {
      this.openInvalidPhoneNumberDialog();
    }
  }

  onMenuItemChange(menuItem: MenuItem): void {
    this.selectedMenuItemId = menuItem.id;
    this.currentVideo = menuItem.video;
    this.headline = menuItem.headLine;
    this.availableIn = { textKey: '', valueKey: '' };
    this.buttonIcon = menuItem.icon;

    if (menuItem.progress === 100) {
      this.availableIn = this.getSurveyAvailableOn(menuItem.availableOn);
    }
  }

  navigateToExplain(): void {
    this.router.navigate(['/explain']);
  }

  navigateToSurvey(): void {
    this.pillService.setSelectedMenuItemById(this.selectedMenuItemId);
    this.router.navigate(['/survey']);
  }

  openHowdyVideo(): void {
    this.showHowdyVideo = true;
    this.fadeIn = false;

    requestAnimationFrame(() => {
      setTimeout(() => {
        this.fadeIn = true;
      }, 10);
    });
  }

  closeHowdyVideo(): void {
    this.fadeOut = true;
    this.fadeIn = false;

    setTimeout(() => {
      this.showHowdyVideo = false;
      this.fadeOut = false;
    }, 400);
  }

  getSurveyAvailableOn(surveyDate: Date | null): AvailableIn {
    if (!surveyDate) {
        return { textKey: '', valueKey: '' };
    }

    const now = new Date();
    const availableOn = new Date(surveyDate);
    const diffInMs = availableOn.getTime() - now.getTime();
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));

    if (diffInHours <= 24 && now.getDate() !== availableOn.getDate()) {
      return { textKey: 'WebApp.v1.AvailableSurveys.Available.Tomorrow', valueKey: '' };
    } else if (diffInHours <= 24 && diffInHours > 1 && now.getDate() === availableOn.getDate()) {
      return {
        textKey: 'WebApp.v1.AvailableSurveys.AvailableIn',
        valueKey: 'WebApp.v1.AvailableSurveys.AvailableIn.Hours',
        params: { count: diffInHours.toString() }
      };
    } else if (diffInHours <= 1 && now.getDate() === availableOn.getDate()) {
      return {
        textKey: 'WebApp.v1.AvailableSurveys.AvailableIn',
        valueKey: 'WebApp.v1.AvailableSurveys.AvailableIn.Minutes',
        params: { count: diffInMinutes.toString() }
      };
    }

    const day = availableOn.getDate();
    const languageCode = this.translationService.getMonthLanguageCode();
    const month = availableOn.toLocaleString(languageCode, { month: 'long' });
    const year = availableOn.getFullYear();

    return {
      textKey: 'WebApp.v1.AvailableSurveys.AvailableOn',
      valueKey: day + '. ' + month + ' ' + year
    };
  }

  openTosDialog(terms: string): void {
    this.hideContent = true;
    this.tosDialogRef = this.dialog.open(TosDialogComponent, {
      disableClose: true,
      width: `${this.tosDialogWidth}px`,
      data: {
        terms
      },
    });

    this.tosDialogRef.closed.subscribe(() => {
      this.hideContent = false;
    });
  }

  openInvalidPhoneNumberDialog(): void {
    this.hideContent = true;
    this.invalidPhoneDialogRef = this.dialog.open(FeatureInvalidPhoneVerificationComponent, {
      disableClose: true,
      width: `${this.tosDialogWidth}px`,
      maxHeight: 'calc(100vh - 190px)'
    });

    this.invalidPhoneDialogRef.closed.subscribe(() => {
      this.hideContent = false;
    });
  }
}
